import { useEffect } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useSemanticColors } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useAuthStore } from '../../lib/stores/auth.store'
import { authService } from '../../lib/services/auth.service'
import { getContactVerificationRequirement } from '../../lib/utils/account-contact'
import { captureError } from '../../lib/utils/observability'
import { isExpectedNonFatalOperationalError } from '../../lib/errors/service-error'
import { authKeys } from '../../lib/hooks/use-auth'

/**
 * Defensive OAuth / deep-link landing. Primary web PKCE exchange lives in
 * RootLayoutApp; this route remains for older links and native handoff.
 * Always: seed session, unlock biometrics, route by contact requirement.
 */
export default function AuthCallbackScreen() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { code } = useLocalSearchParams<{ code?: string }>()

  useEffect(() => {
    let cancelled = false
    const setCompletingSignup = useAuthStore.getState().setCompletingSignup
    const markCredentialUnlock = useAuthStore.getState().markCredentialUnlock

    const finish = async () => {
      setCompletingSignup(true)
      try {
        let session = (await supabase.auth.getSession()).data.session

        if (!session && code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) throw error
          session = data.session
        }

        if (cancelled) return

        if (session) {
          queryClient.setQueryData(authKeys.session(), session)
          markCredentialUnlock()
          if (typeof window !== 'undefined') {
            const hadRegister = window.sessionStorage?.getItem('tago.oauth.register_intent') === '1'
            if (hadRegister && session.user?.id) {
              try {
                await authService.completeSignup(session.user.id, false)
              } catch (err) {
                // Mismo criterio que el handoff social: un fetch cancelado no
                // es un defecto (TAGO-MOBILE-4D).
                if (!isExpectedNonFatalOperationalError(err)) {
                  captureError(err, { screen: 'auth-callback', action: 'complete-signup' })
                }
              }
              window.sessionStorage.removeItem('tago.oauth.register_intent')
            }
          }
          const requirement = getContactVerificationRequirement(session.user, null)
          const route =
            requirement === 'email'
              ? '/(auth)/verify-email'
              : requirement === 'phone'
                ? '/(auth)/verify-phone'
                : '/(tabs)/explore'
          router.replace(route as never)
        } else {
          router.replace('/(auth)/welcome')
        }
      } catch (err) {
        captureError(err, { screen: 'auth-callback', action: 'exchange-code' })
        if (!cancelled) router.replace('/(auth)/welcome')
      }
      // Sin `finally`: la guarda de handoff la baja RootLayoutApp cuando la
      // sesión está viva. Bajarla acá con un temporizador era la carrera que
      // rebotaba a welcome.
    }

    void finish()
    return () => {
      cancelled = true
    }
  }, [code, queryClient, router])

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background,
      }}
    >
      <ActivityIndicator color={colors.primary} />
    </View>
  )
}
